import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "flex items-center gap-2 mb-6" }
const _hoisted_2 = {
  key: 0,
  class: "flex items-center justify-center py-12"
}
const _hoisted_3 = {
  key: 1,
  class: "text-center py-12 text-muted-foreground"
}
const _hoisted_4 = {
  key: 2,
  class: "space-y-8"
}
const _hoisted_5 = { class: "font-semibold text-lg mb-4" }
const _hoisted_6 = { class: "space-y-4" }
const _hoisted_7 = { class: "flex items-start gap-3 flex-wrap" }
const _hoisted_8 = { class: "text-xs text-muted-foreground flex items-center gap-1" }
const _hoisted_9 = {
  key: 0,
  class: "mt-2 text-sm text-muted-foreground whitespace-pre-wrap"
}

import { ref, onMounted } from 'vue'
import { collection, query, where, orderBy, getDocs, limit } from '@/lib/firestore'
import { db } from '@/config/firebase'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { PackageCheck, Loader2, Calendar, Tag } from 'lucide-vue-next'

interface VersionEntry {
  version: string
  type: 'release' | 'pre-release' | 'hotfix' | 'patch'
  notes?: string
  releasedAt: Date
}

interface AppWithVersions {
  id: string
  name: string
  currentVersion: string
  versionHistory: VersionEntry[]
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ChangelogSection',
  setup(__props) {

const isLoading = ref(true)
const apps = ref<AppWithVersions[]>([])

const typeColors: Record<string, string> = {
  release: 'bg-green-500/10 text-green-600 border-green-500/20',
  'pre-release': 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  hotfix: 'bg-red-500/10 text-red-600 border-red-500/20',
  patch: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
}

const typeLabels: Record<string, string> = {
  release: 'Release',
  'pre-release': 'Pre-release',
  hotfix: 'Hotfix',
  patch: 'Patch',
}

const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date)
}

onMounted(async () => {
  try {
    // Fetch applications with version history
    const appsQuery = query(
      collection(db, 'applications'),
      orderBy('updatedAt', 'desc'),
      limit(10)
    )
    const snapshot = await getDocs(appsQuery)

    apps.value = snapshot.docs
      .map((doc) => {
        const data = doc.data()
        const versionHistory = (data.versionHistory || [])
          .map((v: { version: string; type: string; notes?: string; releasedAt?: { toDate?: () => Date } }) => ({
            version: v.version,
            type: v.type as VersionEntry['type'],
            notes: v.notes,
            releasedAt: v.releasedAt?.toDate?.() || new Date(),
          }))
          .sort((a: VersionEntry, b: VersionEntry) => b.releasedAt.getTime() - a.releasedAt.getTime())

        return {
          id: doc.id,
          name: data.name || 'Aplicación',
          currentVersion: data.currentVersion || '0.0.0',
          versionHistory,
        }
      })
      .filter((app) => app.versionHistory.length > 0)
  } catch (error) {
    console.error('Error loading changelog:', error)
  } finally {
    isLoading.value = false
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(_unref(PackageCheck), { class: "h-6 w-6 text-primary" }),
      _cache[0] || (_cache[0] = _createElementVNode("h2", { class: "text-2xl font-bold" }, "Historial de Cambios", -1))
    ]),
    _cache[2] || (_cache[2] = _createElementVNode("p", { class: "text-muted-foreground mb-6" }, " Todas las actualizaciones y mejoras de la plataforma. ", -1)),
    (isLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(_unref(Loader2), { class: "h-6 w-6 animate-spin text-muted-foreground" })
        ]))
      : (apps.value.length === 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(_unref(PackageCheck), { class: "h-12 w-12 mx-auto mb-4 opacity-50" }),
            _cache[1] || (_cache[1] = _createElementVNode("p", null, "No hay historial de cambios disponible", -1))
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_4, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(apps.value, (app) => {
              return (_openBlock(), _createElementBlock("div", {
                key: app.id
              }, [
                _createElementVNode("h3", _hoisted_5, _toDisplayString(app.name), 1),
                _createElementVNode("div", _hoisted_6, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(app.versionHistory.slice(0, 10), (entry, index) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: `${app.id}-${entry.version}`,
                      class: _normalizeClass(["relative pl-6 pb-4", { 'border-l-2 border-muted': index < app.versionHistory.length - 1 }])
                    }, [
                      _createElementVNode("div", {
                        class: _normalizeClass(["absolute left-0 -translate-x-1/2 w-3 h-3 rounded-full bg-primary", index === 0 ? 'ring-4 ring-primary/20' : ''])
                      }, null, 2),
                      _createElementVNode("div", _hoisted_7, [
                        _createVNode(_unref(Badge), {
                          variant: "outline",
                          class: "font-mono"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(" v" + _toDisplayString(entry.version), 1)
                          ]),
                          _: 2
                        }, 1024),
                        _createVNode(_unref(Badge), {
                          class: _normalizeClass(typeColors[entry.type]),
                          variant: "outline"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(typeLabels[entry.type]), 1)
                          ]),
                          _: 2
                        }, 1032, ["class"]),
                        _createElementVNode("span", _hoisted_8, [
                          _createVNode(_unref(Calendar), { class: "h-3 w-3" }),
                          _createTextVNode(" " + _toDisplayString(formatDate(entry.releasedAt)), 1)
                        ])
                      ]),
                      (entry.notes)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_9, _toDisplayString(entry.notes), 1))
                        : _createCommentVNode("", true)
                    ], 2))
                  }), 128))
                ]),
                _createVNode(_unref(Separator), { class: "mt-6" })
              ]))
            }), 128))
          ]))
  ]))
}
}

})